import {
  toCalendarIsoDay,
  resolveProgressDateRaw,
  computedLatestTrustDepositIso,
} from "./tradeDashboardDates";
import {
  isOpenTrade,
  getTradeFinancialReportTotals,
} from "./tradeFinancialUtils";

function isAgentDraft(trade) {
  return (
    trade?.createdByRole === "agent" &&
    !trade?.lockedBySuperUser &&
    trade?.workflowStatus !== "approved"
  );
}

function tradeStatus(trade) {
  return String(trade?.keyInfo?.status || "").toUpperCase();
}

export function isCancelledTrade(trade) {
  return tradeStatus(trade) === "CANCELLED";
}

function agentIdSet(appUser) {
  if (!appUser) return new Set();
  return new Set(
    [appUser.employeeNo, appUser.agentId]
      .map((v) => String(v ?? "").trim())
      .filter(Boolean),
  );
}

export function agentRowMatchesUser(row, appUser) {
  if (!appUser || !row) return false;
  const ids = agentIdSet(appUser);
  const rowId = String(row.agentId ?? "").trim();
  if (rowId && ids.has(rowId)) return true;
  const name = String(appUser.displayName || "").trim().toLowerCase();
  const rowName = String(row.agentName || "").trim().toLowerCase();
  if (name && rowName && (rowName === name || rowName.includes(name))) {
    return true;
  }
  return false;
}

export function tradeBelongsToAgent(trade, appUser) {
  if (!appUser) return true;
  const email = String(appUser.email || "").trim().toLowerCase();
  if (
    email &&
    String(trade?.createdByEmail || "").trim().toLowerCase() === email
  ) {
    return true;
  }
  const list = trade?.agentCommissionList || [];
  return list.some((row) => agentRowMatchesUser(row, appUser));
}

export function commissionAmountForTrade(trade, appUser) {
  if (!appUser || appUser.role !== "agent") {
    const gross = getTradeFinancialReportTotals(trade).grossCommission || 0;
    if (gross > 0) return gross;
    return Number(trade?.paymentReceivedEftAmount) || 0;
  }
  const list = trade?.agentCommissionList || [];
  let sum = 0;
  let matched = false;
  for (const row of list) {
    if (!agentRowMatchesUser(row, appUser)) continue;
    matched = true;
    sum +=
      parseFloat(row.amount || row.awardAmount || row.netCommission || 0) || 0;
  }
  if (matched) return sum;
  return getTradeFinancialReportTotals(trade).grossCommission || 0;
}

export function tradeIdFromEft(eft) {
  if (!eft) return "";
  if (eft.tradeId && typeof eft.tradeId === "object") {
    return String(eft.tradeId._id || eft.tradeId.id || "").trim();
  }
  return String(eft.tradeId || "").trim();
}

export function eftBelongsToAgent(eft, appUser, agentTradeIds) {
  if (!appUser || appUser.role !== "agent") return true;
  const ids = agentIdSet(appUser);
  const eftAgent = String(eft?.agentId ?? "").trim();
  if (eftAgent && ids.has(eftAgent)) return true;
  const name = String(appUser.displayName || "").trim().toLowerCase();
  const eftName = String(eft?.agentName || "").trim().toLowerCase();
  if (name && eftName && (eftName === name || eftName.includes(name))) {
    return true;
  }
  const tradeId = tradeIdFromEft(eft);
  if (tradeId && agentTradeIds && agentTradeIds.has(tradeId)) return true;
  return false;
}

export function isClosedTrade(trade) {
  if (isCancelledTrade(trade)) return false;
  if (tradeStatus(trade) === "CLOSED") return true;
  if (trade?.isFinalized) return true;
  return false;
}

export function getTradeCloseIso(trade) {
  const k = trade?.keyInfo || {};
  return (
    toCalendarIsoDay(k.closeDate) ||
    toCalendarIsoDay(trade?.commission?.saleClosingRows?.[0]?.closingDate) ||
    toCalendarIsoDay(k.finalizedDate) ||
    toCalendarIsoDay(k.closedTRSDate) ||
    toCalendarIsoDay(trade?.progressTracker?.closedTRSDate) ||
    null
  );
}

export function getPaymentReceivedIso(trade) {
  const eftIso = String(trade?.paymentReceivedEftDateIso || "").trim();
  if (eftIso) return toCalendarIsoDay(eftIso) || eftIso;
  const stored = resolveProgressDateRaw(trade, "paymentReceivedDate");
  return toCalendarIsoDay(stored) || computedLatestTrustDepositIso(trade) || null;
}

function isoYearMonth(iso) {
  if (!iso || iso.length < 7) return "";
  return iso.slice(0, 7);
}

export function formatStatMoney(value) {
  const n = Number(value) || 0;
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function formatStatDate(value) {
  if (!value) return "";
  // Always format via calendar YYYY-MM-DD so ISO date-only strings
  // (e.g. "2026-08-20") are not shifted a day by UTC parsing.
  const iso = toCalendarIsoDay(value);
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  const local = new Date(y, m - 1, d);
  if (Number.isNaN(local.getTime())) return "";
  return local.toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  });
}

export function computeDashboardStats(
  trades,
  agents,
  lastReconciliationDate,
  appUser,
) {
  const today = new Date();
  const todayIso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  const yearPrefix = String(today.getFullYear());
  const monthPrefix = isoYearMonth(todayIso);
  const agentScoped = appUser?.role === "agent";

  const scopedTrades = agentScoped
    ? (trades || []).filter((t) => tradeBelongsToAgent(t, appUser))
    : trades || [];

  const countable = scopedTrades.filter((t) => {
    if (isCancelledTrade(t)) return false;
    if (!agentScoped && isAgentDraft(t)) return false;
    return true;
  });

  let grossCommissionReceivedYtd = 0;
  let pendingReceipts = 0;
  let tradesOpen = 0;
  let tradesClosingThisMonth = 0;
  let tradesClosingToday = 0;
  let tradesClosedYtd = 0;

  for (const trade of countable) {
    const gross = commissionAmountForTrade(trade, agentScoped ? appUser : null);
    const paymentIso = getPaymentReceivedIso(trade);
    const closeIso = getTradeCloseIso(trade);
    const open = isOpenTrade(trade) && !isClosedTrade(trade);
    const receipts = !agentScoped ? trade.commissionReceipts : null;
    const receiptYtd = Array.isArray(receipts)
      ? receipts.reduce((sum, rec) => {
          const iso = String(rec?.dateIso || "").trim();
          if (!iso.startsWith(yearPrefix)) return sum;
          return sum + (Number(rec.amount) || 0);
        }, 0)
      : 0;

    if (receiptYtd > 0) {
      grossCommissionReceivedYtd += receiptYtd;
    } else if (paymentIso && paymentIso.startsWith(yearPrefix)) {
      grossCommissionReceivedYtd += gross;
    } else if (open) {
      pendingReceipts += gross;
    }

    if (open) {
      tradesOpen += 1;
      if (closeIso && isoYearMonth(closeIso) === monthPrefix) {
        tradesClosingThisMonth += 1;
      }
      if (closeIso === todayIso) {
        tradesClosingToday += 1;
      }
    }

    if (isClosedTrade(trade)) {
      const closedIso =
        closeIso || toCalendarIsoDay(trade?.keyInfo?.finalizedDate);
      if (closedIso && closedIso.startsWith(yearPrefix)) {
        tradesClosedYtd += 1;
      }
    }
  }

  const activeAgents = (agents || []).filter((agent) => {
    const status = String(agent?.status || "Active").trim().toLowerCase();
    return status === "active";
  }).length;

  return {
    grossCommissionReceivedYtd,
    pendingReceipts,
    lastReconciliationDone: formatStatDate(lastReconciliationDate),
    activeAgents,
    tradesOpen,
    tradesClosingThisMonth,
    tradesClosingToday,
    tradesClosedYtd,
  };
}
